import { PlanDetails } from '@app/catalog/category/plans/blade/blade.interface';
import { Scheme } from '@app/checkout/cart/scheme.class';

import { BtlHelperService } from '../../../base/services/btl-helper.service';
import { PlanBase } from '../../../baseBtl/catalog/products/subscription/plan';
import { RenewContextServiceBase } from '../../../baseBtl/context/child/renew-context.service';
import { JsonProduct } from '../interface/context';

export class Plan extends PlanBase {
    private readonly PLAN_BONUS_STATUS = {
        ANY: 0,
        ONE: 1,
        ALL: 2,
    };

    public static readonly ANCHORS = {
        BONUS: 'bonusAnchor',
        ADVANTAGES: 'advantagesAnchor',
    };

    public renew_prices: any;
    public promoDuration: number;

    public getDetailAttributes() {
        const detailData: JsonProduct = this.getJsonData();
        return {
            smsMms: detailData.sms_mms,
            fromAbroad: detailData.from_abroad,
            internationalCall: detailData.international_call,
            unlimitedCall: detailData.unlimited_call,
            planBonus: detailData.plan_bonus,
            planBonusStatus: detailData.plan_bonus_status,
            doubleSim: detailData.double_sim,
            smartphoneAdvantage: detailData.smartphone_advantage,
            optionBtv: detailData.wording_btv,
            proPack: detailData.pack_pro,
        };
    }

    public getDetailArray(hidePackPro?: boolean) {
        const details: any[] = [];
        const planDetail = this.getDetailAttributes();

        if (planDetail.smsMms) {
            planDetail.smsMms = BtlHelperService.removeHtml(planDetail.smsMms, ['br']);
            details.push({
                title: "En France et depuis l'étranger",
                values: this.formatValues(planDetail.smsMms),
                btlLabel: 'plan_details.sms_sms',
            });
        }

        if (planDetail.fromAbroad) {
            details.push({
                title: 'Internet mobile',
                values: this.formatValues(planDetail.fromAbroad, 'zone Business Pro'),
                modal: 'zone_forfaits-zone_business_pro',
                btlLabel: 'plan_details.depuis_etranger',
            });
        }

        if (planDetail.proPack && !hidePackPro) {
            planDetail.proPack.split(';').forEach((value) => {
                details.push({
                    title: 'Pack pro',
                    values: [{ value, isLink: false }],
                    btlLabel: 'plan_details.pack_pro',
                });
            });
        }

        if (planDetail.smartphoneAdvantage) {
            const content = 'Tous les avantages smartphone';
            details.push({
                title: 'Avantages multi-ligne',
                values: this.formatValues(content, content),
                modal: 'zone_forfaits-smartphone_advantages',
                btlLabel: 'plan_details.avantages_smartphone',
            });
        }

        if (planDetail.planBonus) {
            let value;
            const bonusData: { [index: string]: any } = {
                title: 'Bonus inclus',
                btlLabel: 'plan_details.bonus_inclus',
            };

            switch (planDetail.planBonus) {
                case '0':
                case 'Pas de bonus':
                    break;
                case '1':
                    value = '1 bonus au choix (liste disponible sur votre espace client)';
                    break;
                case '2':
                    value = 'Tous les bonus (liste disponible sur votre espace client)';
                    break;
                default:
                    value =
                        planDetail.planBonusStatus === this.PLAN_BONUS_STATUS.ALL
                            ? 'Tous les bonus'
                            : planDetail.planBonus;
                    bonusData.modal = 'zone_forfaits-bonus_inclus';
                    break;
            }

            if (value) {
                bonusData.values = this.formatValues(value, bonusData.modal ? value : null);
                details.push(bonusData);
            }
        }

        if (planDetail.doubleSim && planDetail.doubleSim !== '0') {
            const content = '2ème carte SIM Internet offerte';
            details.push({
                title: 'Option incluse',
                values: this.formatValues(content),
                btlLabel: 'plan_details.double_sim',
            });
        }

        if (planDetail.optionBtv) {
            details.push({
                title: 'Vos services inclus',
                values: this.formatValues(planDetail.optionBtv),
                btlLabel: 'plan_details.option_btv',
            });
        }

        return details;
    }

    public getStructuredDetails(): PlanDetails {
        const advantagesLabels = [
            'plan_details.bonus_inclus',
            'plan_details.double_sim',
            'plan_details.avantages_smartphone',
            'plan_details.option_btv',
        ];
        const details = this.getDetailArray();
        const included = details.filter(
            (detail) => !advantagesLabels.includes(detail.btlLabel) && detail.btlLabel !== 'plan_details.pack_pro',
        );
        const advantages = details.filter(
            (detail) => advantagesLabels.includes(detail.btlLabel) && detail.btlLabel !== 'plan_details.pack_pro',
        );
        const proPack = details.filter((detail) => detail.btlLabel === 'plan_details.pack_pro');
        return { included, advantages, proPack };
    }

    private formatValues(content: string, word?: string): any[] {
        const values = [];
        if (word && content.includes(word)) {
            const array = content.split(word);
            array.forEach((desc, index) => {
                values.push({ value: desc, isLink: false });
                if (index < array.length - 1) {
                    values.push({ value: word, isLink: true });
                }
            });
        } else {
            values.push({ value: content, isLink: false });
        }
        return values;
    }

    public getOptionText() {
        const optionsText = this.getJsonData().option_text;
        let options;
        if (optionsText) {
            options = [{ title: 'Options', values: this.formatValues(optionsText) }];
        }

        return options;
    }

    public getPlanFlag() {
        const optionsData: JsonProduct = this.getJsonData();
        return optionsData.plan_flag;
    }

    public getPlanFlagInfos() {
        const optionsData: JsonProduct = this.getJsonData();
        return optionsData.plan_flag_infos;
    }

    splitDescription(text: string, keyWord: string): string[] {
        return text.split(keyWord);
    }

    protected updatePrice(): void {
        const renewContext: RenewContextServiceBase =
            this.context.contextService.get<RenewContextServiceBase>(RenewContextServiceBase);
        if (renewContext) {
            const gallium_prices = renewContext
                .getRenewStorageService()
                .getItem(renewContext.getRenewStorageService()['LS_KEY']['GALLIUM_PRICES']);

            this.renew_prices = gallium_prices && gallium_prices[this.gencode] ? gallium_prices[this.gencode] : null;
            this.price = this.renew_prices ? this.renew_prices.price : super.getPrice();
            this.duration = this.obligation ? Number(this.obligation.replace(/[^\d]/g, '')) : null;
        } else {
            super.updatePrice();
        }
    }

    /**
     * Add plan gencode in context for EDP contextualisation
     * @param scheme
     * @returns
     */
    public updateSchemeOnAdd(scheme: Scheme): Scheme {
        scheme.productContext.plan = this.gencode;
        scheme.updateContext(scheme.productContext);
        return scheme;
    }

    /**
     * Remove plan gencode in context if you remove plan
     * @param scheme
     * @returns
     */
    public updateSchemeOnRemove(scheme: Scheme): Scheme {
        super.updateSchemeOnRemove(scheme);
        delete scheme.productContext.plan;
        scheme.updateContext(scheme.productContext);
        return scheme;
    }
}
